import { ref, computed, watch, onMounted } from 'vue';
import CannedResponseAPI from 'dashboard/api/cannedResponse';
import OrderIssueTagsAPI from 'dashboard/api/orderIssueTags';

/**
 * Centralized composable for managing Order Issue tags with template-based filtering
 * This fetches tags from BOTH templates AND the database (custom tags)
 */
export default function useOrderIssueTags() {
  // All available tags (merged from templates + database)
  const allProblemTags = ref([]);
  const allActionTags = ref([]);
  
  // Template combinations (problem_tag -> action_tags mapping)
  const templateCombinations = ref(new Map());
  
  // Currently selected tags
  const selectedProblemTag = ref(null);
  const selectedActionTag = ref(null);
  
  // Filtered options based on selection
  // Note: templateCombinations uses tag NAMES as keys, not IDs
  const filteredProblemTagOptions = computed(() => {
    if (!selectedActionTag.value) {
      return allProblemTags.value;
    }
    
    // Get action tag NAME for lookup
    const actionTagName = selectedActionTag.value.name || selectedActionTag.value.id || selectedActionTag.value;
    
    // Filter problem tags that have combinations with the selected action tag
    const validProblemTags = new Set();
    for (const [problemTagName, actionTagNames] of templateCombinations.value.entries()) {
      if (actionTagNames.includes(actionTagName)) {
        validProblemTags.add(problemTagName);
      }
    }
    
    return allProblemTags.value.filter(tag => 
      validProblemTags.has(tag.name)
    );
  });
  
  const filteredActionTagOptions = computed(() => {
    if (!selectedProblemTag.value) {
      return allActionTags.value;
    }
    
    // Get problem tag NAME for lookup (combinations use names as keys)
    const problemTagName = selectedProblemTag.value.name || selectedProblemTag.value.id || selectedProblemTag.value;
    const validActionTagNames = templateCombinations.value.get(problemTagName) || [];
    
    return allActionTags.value.filter(tag => 
      validActionTagNames.includes(tag.name)
    );
  });
  
  /**
   * Merge tags from two sources, avoiding duplicates
   */
  const mergeTags = (templateTags, dbTags) => {
    const tagMap = new Map();
    
    // Add template tags first
    (templateTags || []).forEach(tag => {
      const key = tag.id || tag.name || tag;
      tagMap.set(key, { id: key, name: tag.name || key });
    });
    
    // Add database tags (will overwrite if same name, which is fine)
    (dbTags || []).forEach(tag => {
      const key = tag.name || tag.id || tag;
      tagMap.set(key, { id: tag.id || key, name: tag.name || key });
    });
    
    return Array.from(tagMap.values());
  };
  
  /**
   * Fetch all tags from BOTH templates AND database
   */
  const fetchTags = async () => {
    try {
      // Fetch from both sources in parallel
      const [templateResponse, dbResponse] = await Promise.all([
        CannedResponseAPI.getTags().catch(() => ({ data: {} })),
        OrderIssueTagsAPI.getAll().catch(() => ({ data: {} })),
      ]);
      
      // Template tags
      const templateProblemTags = templateResponse.data?.problem_tags || [];
      const templateActionTags = templateResponse.data?.action_tags || [];
      
      // Database tags (custom tags from Order Issues page)
      const dbProblemTags = (dbResponse.data?.problem_tags || []);
      const dbActionTags = (dbResponse.data?.action_tags || []);
      
      // Merge both sources
      allProblemTags.value = mergeTags(templateProblemTags, dbProblemTags);
      allActionTags.value = mergeTags(templateActionTags, dbActionTags);
      
      // Build combinations map from BOTH sources
      const combos = new Map();
      
      // Add template combinations
      if (templateResponse.data?.combinations) {
        templateResponse.data.combinations.forEach(combo => {
          if (!combos.has(combo.problem_tag)) {
            combos.set(combo.problem_tag, []);
          }
          if (!combos.get(combo.problem_tag).includes(combo.action_tag)) {
            combos.get(combo.problem_tag).push(combo.action_tag);
          }
        });
      }
      
      // Add database combinations (uses IDs, need to map to names)
      if (dbResponse.data?.combinations) {
        // Create ID -> name maps
        const problemIdToName = new Map();
        const actionIdToName = new Map();
        dbProblemTags.forEach(t => problemIdToName.set(t.id, t.name));
        dbActionTags.forEach(t => actionIdToName.set(t.id, t.name));
        
        dbResponse.data.combinations.forEach(combo => {
          const problemName = problemIdToName.get(combo.problem_tag_id);
          const actionName = actionIdToName.get(combo.action_tag_id);
          if (problemName && actionName) {
            if (!combos.has(problemName)) {
              combos.set(problemName, []);
            }
            if (!combos.get(problemName).includes(actionName)) {
              combos.get(problemName).push(actionName);
            }
          }
        });
      }
      
      templateCombinations.value = combos;
    } catch (error) {
      console.error('Failed to fetch order issue tags:', error);
    }
  };
  
  /**
   * Initialize tags on mount
   */
  onMounted(() => {
    fetchTags();
  });
  
  /**
   * Watch for problem tag changes and clear action tag if invalid
   * Note: Uses tag NAMES for combination lookups
   */
  watch(selectedProblemTag, (newProblemTag) => {
    if (newProblemTag && selectedActionTag.value) {
      const problemTagName = newProblemTag.name || newProblemTag.id || newProblemTag;
      const validActionTagNames = templateCombinations.value.get(problemTagName) || [];
      const actionTagName = selectedActionTag.value.name || selectedActionTag.value.id || selectedActionTag.value;
      
      if (!validActionTagNames.includes(actionTagName)) {
        selectedActionTag.value = null;
      }
    }
  });
  
  /**
   * Watch for action tag changes and clear problem tag if invalid
   * Note: Uses tag NAMES for combination lookups
   */
  watch(selectedActionTag, (newActionTag) => {
    if (newActionTag && selectedProblemTag.value) {
      const actionTagName = newActionTag.name || newActionTag.id || newActionTag;
      const problemTagName = selectedProblemTag.value.name || selectedProblemTag.value.id || selectedProblemTag.value;
      let isValid = false;
      
      for (const [problemName, actionNames] of templateCombinations.value.entries()) {
        if (actionNames.includes(actionTagName) && problemName === problemTagName) {
          isValid = true;
          break;
        }
      }
      
      if (!isValid) {
        selectedProblemTag.value = null;
      }
    }
  });
  
  /**
   * Add a custom problem tag
   */
  const addProblemTag = (newTag) => {
    const tag = { id: newTag, name: newTag };
    allProblemTags.value.push(tag);
    selectedProblemTag.value = tag;
  };
  
  /**
   * Add a custom action tag
   */
  const addActionTag = (newTag) => {
    const tag = { id: newTag, name: newTag };
    allActionTags.value.push(tag);
    selectedActionTag.value = tag;
  };
  
  /**
   * Set tags from existing values (for edit mode)
   */
  const setTags = (problemTag, actionTag) => {
    const tagToObject = (tag) => {
      if (!tag) return null;
      if (typeof tag === 'object') return tag;
      return { id: tag, name: tag };
    };
    
    selectedProblemTag.value = tagToObject(problemTag);
    selectedActionTag.value = tagToObject(actionTag);
  };
  
  /**
   * Get string values for API submission
   * Always use NAME (not numeric ID) for saving
   */
  const getTagValues = () => {
    return {
      problem_tag: selectedProblemTag.value?.name || '',
      action_tag: selectedActionTag.value?.name || '',
    };
  };
  
  return {
    // State
    selectedProblemTag,
    selectedActionTag,
    
    // Filtered options
    filteredProblemTagOptions,
    filteredActionTagOptions,
    
    // Methods
    fetchTags,
    addProblemTag,
    addActionTag,
    setTags,
    getTagValues,
  };
}
